/*
    Универсальный Flowmap с:
    1. Локальным искажением (линза)
    2. Большой областью влияния (~40%)
    3. Усилением волн при наведении
    4. Плавным переходом позиции мыши и затуханием эффекта
    5. Отслеживание мыши по всему документу с Intersection Observer

    ПОДДЕРЖКА МНОЖЕСТВЕННЫХ КОНТЕЙНЕРОВ + ДЕТЕКЦИЯ СТАРОГО SAFARI
  */

const vertex = `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0, 1);
  }
`;

const fragment = `
  precision highp float;
  precision highp int;

  uniform sampler2D tWater;
  uniform sampler2D tFlow;
  uniform float uTime;
  uniform vec2 uMouse;
  uniform float uMouseStrength;
  varying vec2 vUv;
  uniform vec4 res;
  uniform vec2 img;

  void main() {
    vec3 flow = texture2D(tFlow, vUv).rgb;

    vec2 uv = .5 * gl_FragCoord.xy / res.xy;
    vec2 myUV = (uv - vec2(0.5)) * res.zw + vec2(0.5);

    float aspect = res.x / res.y;
    vec2 cursorDiff = vUv - uMouse;
    float dist = length(cursorDiff * vec2(aspect, 1.0));

    float outerRadius = 0.6;
    float innerRadius = 0.15;
    float strength = 0.024 * uMouseStrength;

    float ringMask = 0.0;

    if (dist > innerRadius && dist < outerRadius) {
        float t = (dist - innerRadius) / (outerRadius - innerRadius);
        ringMask = sin(t * 3.14159265);
        ringMask = pow(ringMask, 2.0);
    }

    if (dist > 0.001) {
        vec2 dir = normalize(cursorDiff);
        myUV -= dir * ringMask * strength;
    }

    myUV -= flow.xy * 0.012;

    float waveMask = smoothstep(outerRadius, 0.0, dist);
    float waveBoost = 1.0 + (waveMask * 0.3 * uMouseStrength);

    float wave1 = sin(myUV.x * 4.0 + uTime * 0.6) * 0.016 * waveBoost;
    float wave2 = sin(myUV.x * 7.0 - uTime * 0.4) * 0.012 * waveBoost;
    float wave3 = sin(myUV.x * 10.0 + uTime * 0.3) * 0.009 * waveBoost;
    float wave4 = sin(myUV.x * 13.0 - uTime * 0.5) * 0.007 * waveBoost;
    float wave5 = sin(myUV.x * 16.0 + uTime * 0.35) * 0.005 * waveBoost;
    float wave6 = sin(myUV.x * 20.0 - uTime * 0.45) * 0.004 * waveBoost;

    myUV.y += wave1 + wave2 + wave3 + wave4 + wave5 + wave6;

    vec3 tex = texture2D(tWater, myUV).rgb;
    gl_FragColor = vec4(tex.r, tex.g, tex.b, 1.0);
  }
`;

// Функция детекции старого Safari
function detectOldSafari() {
  const ua = navigator.userAgent;
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua);

  if (!isSafari) return false;

  const versionMatch = ua.match(/Version\/(\d+)\.(\d+)/);
  if (!versionMatch) return false;

  const majorVersion = parseInt(versionMatch[1], 10);
  const minorVersion = parseInt(versionMatch[2], 10);

  if (majorVersion < 15) return true;
  if (majorVersion === 15 && minorVersion <= 6) return true;

  return false;
}

// Функция проверки поддержки WebGL
function checkWebGLSupport() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return !!gl;
  } catch (e) {
    return false;
  }
}

// Функция инициализации для одного контейнера
async function initCanvasContainer(container) {
  const isOldSafari = detectOldSafari();
  const hasWebGL = checkWebGLSupport();

  if (isOldSafari || !hasWebGL) {
    container.classList.add('is-old-browser');
    console.warn('Old Safari or no WebGL detected, added is-old-browser class');
  }

  if (!hasWebGL) {
    console.error('WebGL not supported');
    return;
  }

  try {
    const renderer = new ogl.Renderer({ dpr: 2 });
    const gl = renderer.gl;

    const canvasWrapper = container.querySelector('.edge-canvas-container__canvas');
    canvasWrapper.appendChild(gl.canvas);

    const geometry = new ogl.Geometry(gl, {
      position: { size: 2, data: new Float32Array([-1, -1, 3, -1, -1, 3]) },
      uv: { size: 2, data: new Float32Array([0, 0, 2, 0, 0, 2]) }
    });

    const flowmap = new ogl.Flowmap(gl);

    let aspect = 1;
    const mouse = new ogl.Vec2(-1);
    const velocity = new ogl.Vec2();
    const lastMouse = new ogl.Vec2();
    let lastTime;

    // Переменные для плавной интерполяции
    const targetMouse = new ogl.Vec2(-10, -10);
    const currentMouse = new ogl.Vec2(-10, -10);
    let mouseStrength = 0;
    let isIntersecting = false; // Контейнер видим в viewport?

    const video = container.querySelector('.video-bg');
    const staticImage = container.querySelector('.static-bg');

    let useVideo = false;
    let mediaSource = null;

    // Intersection Observer для отслеживания видимости контейнера
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // Контейнер считается видимым если хотя бы 10% в viewport
    };

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        isIntersecting = entry.isIntersecting;
      });
    }, observerOptions);

    // Начинаем наблюдение за контейнером
    observer.observe(container);

    async function checkVideoAvailability() {
      return new Promise(function (resolve) {
        if (!video || !video.querySelector('source')) {
          resolve(false);
          return;
        }

        const timeout = setTimeout(function () {
          resolve(false);
        }, 3000);

        function onCanPlay() {
          clearTimeout(timeout);
          cleanup();
          resolve(true);
        }

        function onError() {
          clearTimeout(timeout);
          cleanup();
          resolve(false);
        }

        function cleanup() {
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('error', onError);
        }

        video.addEventListener('canplay', onCanPlay, { once: true });
        video.addEventListener('error', onError, { once: true });

        video.load();

        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(function () { resolve(false); });
        }
      });
    }

    const videoAvailable = await checkVideoAvailability();

    if (videoAvailable && video.videoWidth > 0) {
      useVideo = true;
      mediaSource = video;
      console.log('Using video source for container');
    } else if (staticImage) {
      useVideo = false;
      mediaSource = staticImage;
      console.log('Using static image source for container');

      if (!staticImage.complete) {
        await new Promise(function (resolve) {
          staticImage.addEventListener('load', resolve, { once: true });
          staticImage.addEventListener('error', resolve, { once: true });
        });
      }
    } else {
      console.error('No media source available for container');
      container.classList.add('is-old-browser');
      return;
    }

    const offscreen = document.createElement('canvas');
    const offCtx = offscreen.getContext('2d');

    if (useVideo) {
      async function waitFirstFrame() {
        return new Promise(function (resolve) {
          if (video.requestVideoFrameCallback) {
            video.requestVideoFrameCallback(function () { resolve(); });
          } else {
            video.addEventListener('loadeddata', function () { resolve(); }, { once: true });
          }

          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(function () { });
          }
        });
      }
      await waitFirstFrame();

      async function ensureVideoSize(maxTries) {
        maxTries = maxTries || 10;
        return new Promise(function (resolve) {
          let tries = 0;
          (function check() {
            if (video.videoWidth > 0 && video.videoHeight > 0) return resolve();
            tries++;
            if (tries > maxTries) {
              console.warn('Video size not available after max tries');
              return resolve();
            }
            requestAnimationFrame(check);
          })();
        });
      }
      await ensureVideoSize();

      offscreen.width = Math.max(1, video.videoWidth || 1);
      offscreen.height = Math.max(1, video.videoHeight || 1);
    } else {
      offscreen.width = Math.max(1, staticImage.naturalWidth || staticImage.width || 1);
      offscreen.height = Math.max(1, staticImage.naturalHeight || staticImage.height || 1);
    }

    try {
      offCtx.drawImage(mediaSource, 0, 0, offscreen.width, offscreen.height);
    } catch (e) {
      console.warn('Initial drawImage failed', e);
      container.classList.add('is-old-browser');
    }

    const texture = new ogl.Texture(gl, {
      image: offscreen,
      minFilter: gl.LINEAR,
      magFilter: gl.LINEAR,
      generateMipmaps: false
    });

    const imgUniform = new ogl.Vec2(offscreen.width, offscreen.height);

    function calcResForImage(imgW, imgH) {
      let a1, a2;
      const imageAspect = imgH / imgW;
      if (window.innerHeight / window.innerWidth < imageAspect) {
        a1 = 1;
        a2 = window.innerHeight / window.innerWidth / imageAspect;
      } else {
        a1 = (window.innerWidth / window.innerHeight) * imageAspect;
        a2 = 1;
      }
      return { a1: a1, a2: a2 };
    }

    const resCalc = calcResForImage(offscreen.width, offscreen.height);

    const program = new ogl.Program(gl, {
      vertex: vertex,
      fragment: fragment,
      uniforms: {
        uTime: { value: 0 },
        tWater: { value: texture },
        tFlow: flowmap.uniform,
        res: { value: new ogl.Vec4(window.innerWidth, window.innerHeight, resCalc.a1, resCalc.a2) },
        img: { value: imgUniform },
        uMouse: { value: new ogl.Vec2(-10, -10) },
        uMouseStrength: { value: 0 }
      }
    });

    const mesh = new ogl.Mesh(gl, { geometry: geometry, program: program });

    function resize() {
      const imgW = offscreen.width || 1;
      const imgH = offscreen.height || 1;
      const resCalc = calcResForImage(imgW, imgH);

      mesh.program.uniforms.res.value = new ogl.Vec4(window.innerWidth, window.innerHeight, resCalc.a1, resCalc.a2);
      renderer.setSize(window.innerWidth, window.innerHeight);
      aspect = window.innerWidth / window.innerHeight;
    }
    window.addEventListener('resize', resize, false);
    resize();

    const isTouchCapable = 'ontouchstart' in window;

    // Функция обновления позиции мыши относительно ДОКУМЕНТА
    function updateMouse(e) {
      // Получаем координаты курсора
      let clientX, clientY;

      if (e.changedTouches && e.changedTouches.length) {
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      // Получаем позицию и размеры контейнера относительно viewport
      const rect = container.getBoundingClientRect();

      // Вычисляем позицию мыши относительно КОНТЕЙНЕРА
      const containerX = clientX - rect.left;
      const containerY = clientY - rect.top;

      // Нормализуем координаты (0..1) относительно размеров контейнера
      const normalizedX = containerX / rect.width;
      const normalizedY = 1.0 - (containerY / rect.height);

      // Обновляем целевую позицию мыши
      targetMouse.set(normalizedX, normalizedY);

      // Для flowmap (используем координаты относительно renderer)
      const canvasRect = gl.canvas.getBoundingClientRect();
      mouse.set(
        (clientX - canvasRect.left) / canvasRect.width,
        1.0 - (clientY - canvasRect.top) / canvasRect.height
      );

      if (!lastTime) {
        lastTime = performance.now();
        lastMouse.set(containerX, containerY);
      }

      const deltaX = containerX - lastMouse.x;
      const deltaY = containerY - lastMouse.y;

      lastMouse.set(containerX, containerY);

      let time = performance.now();
      let delta = Math.max(10.4, time - lastTime);
      lastTime = time;

      velocity.x = deltaX / delta;
      velocity.y = deltaY / delta;
      velocity.needsUpdate = true;
    }

    // Слушаем события на DOCUMENT, а не на контейнере
    if (isTouchCapable) {
      document.addEventListener('touchstart', updateMouse, { passive: true });
      document.addEventListener('touchmove', updateMouse, { passive: true });
    } else {
      document.addEventListener('mousemove', updateMouse, { passive: true });
    }

    function update(t) {
      requestAnimationFrame(update);

      if (useVideo) {
        try {
          const vw = Math.max(1, video.videoWidth || offscreen.width);
          const vh = Math.max(1, video.videoHeight || offscreen.height);

          if (offscreen.width !== vw || offscreen.height !== vh) {
            offscreen.width = vw;
            offscreen.height = vh;
            mesh.program.uniforms.img.value = new ogl.Vec2(vw, vh);
            resize();
          }

          offCtx.drawImage(video, 0, 0, offscreen.width, offscreen.height);
          texture.image = offscreen;
          texture.needsUpdate = true;
        } catch (err) {
          if (isOldSafari) {
            console.warn('Video rendering issue on old Safari:', err);
          }
        }
      }

      if (offscreen.width === 0 || offscreen.height === 0) return;

      // Плавное затухание/появление силы эффекта ТОЛЬКО когда контейнер видим
      const strengthSpeed = 0.08;
      if (isIntersecting) {
        mouseStrength += (1 - mouseStrength) * strengthSpeed;
        if (mouseStrength > 0.99) mouseStrength = 1;
      } else {
        mouseStrength += (0 - mouseStrength) * strengthSpeed;
        if (mouseStrength < 0.01) mouseStrength = 0;
      }

      // Плавная интерполяция позиции мыши
      const lerpSpeed = 0.15;
      currentMouse.x += (targetMouse.x - currentMouse.x) * lerpSpeed;
      currentMouse.y += (targetMouse.y - currentMouse.y) * lerpSpeed;

      // Передаем интерполированную позицию в шейдер
      program.uniforms.uMouse.value.set(currentMouse.x, currentMouse.y);
      program.uniforms.uMouseStrength.value = mouseStrength;

      if (!velocity.needsUpdate) {
        mouse.set(-1);
        velocity.set(0);
      }

      velocity.needsUpdate = false;

      flowmap.aspect = aspect;
      flowmap.mouse.copy(mouse);
      flowmap.velocity.lerp(velocity, velocity.len ? 0.15 : 0.1);

      flowmap.update();
      program.uniforms.uTime.value = t * 0.001;

      renderer.render({ scene: mesh });
    }

    requestAnimationFrame(update);

  } catch (error) {
    console.error('Canvas initialization failed:', error);
    container.classList.add('is-old-browser');
  }
}

// Инициализация всех контейнеров на странице
(async function () {
  const containers = document.querySelectorAll('.edge-canvas-container');

  for (let i = 0; i < containers.length; i++) {
    await initCanvasContainer(containers[i]);
  }
})();